import React from 'react';
import {
  ColumnActionsMode,
  IColumn,
  ITooltipHostStyles,
  TooltipDelay,
  TooltipHost,
} from '@fluentui/react';
import { Link } from 'react-router-dom';
import { CheckmarkCircleColor } from '@fluentui/react-icons';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';
import { IEntityTerminationActionItem } from '../types';

const tooltipHostStyles: Partial<ITooltipHostStyles> = {
  root: { textOverflow: 'ellipsis', overflow: 'hidden' },
};

const onRenderDescription = (item: IEntityTerminationActionItem): JSX.Element => {
  return (
    <TooltipHost content={item?.description} id="tooltip">
      <span>{item?.description}</span>
    </TooltipHost>
  );
};

const onRenderCompletedStatus = (item: IEntityTerminationActionItem): JSX.Element => {
  const hasDateCompleted = !!item?.dateCompleted;

  return (
    <>
      {hasDateCompleted && (
        <CheckmarkCircleColor fontSize={20} style={{ verticalAlign: 'middle' }} />
      )}
    </>
  );
};

const onRenderOwner = (item: IEntityTerminationActionItem): JSX.Element => {
  return (
    <TooltipHost
      delay={TooltipDelay.medium}
      key={item.id}
      styles={tooltipHostStyles}
      tooltipProps={{
        onRenderContent: () => {
          return <span>{item.owner?.text}</span>;
        },
      }}
    >
      {item.owner?.text}
    </TooltipHost>
  );
};

const onRenderLegoCategory = (
  item: IEntityTerminationActionItem,
  companyCode: string,
): JSX.Element => {
  return (
    <TooltipHost content={item.legoCategory?.fieldName} id="tooltip">
      <Link
        to={{
          pathname: `/monitoring-queue/${companyCode}/blocker/actionItem/${item.id}`,
          state: {
            selectedActionItem: item,
          },
        }}
      >
        {item.legoCategory?.fieldName}
      </Link>
    </TooltipHost>
  );
};

const getColumns = (companyCode: string): Array<IColumn> => [
  {
    key: 'completedStatus',
    name: 'Completion Status',
    fieldName: 'completedStatus',
    minWidth: 30,
    maxWidth: 30,
    isResizable: false,
    onRender: (item) => onRenderCompletedStatus(item),
  },
  {
    key: 'legoCategory',
    name: 'Blocker Category',
    fieldName: 'legoCategory',
    minWidth: 200,
    maxWidth: 200,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => onRenderLegoCategory(item, companyCode),
  },
  {
    key: 'description',
    name: 'Description',
    fieldName: 'description',
    minWidth: 200,
    maxWidth: 200,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => onRenderDescription(item),
  },
  {
    key: 'isPrimaryBlocker',
    name: 'Primary Blocker',
    fieldName: 'isPrimaryBlocker',
    minWidth: 120,
    maxWidth: 120,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item: IEntityTerminationActionItem) => (
      <span>{item.isPrimaryBlocker ? 'Yes' : 'No'}</span>
    ),
  },
  {
    key: 'owner',
    name: 'Owner',
    fieldName: 'owner',
    minWidth: 200,
    maxWidth: 200,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => onRenderOwner(item),
  },
  {
    key: 'dateStarted',
    name: 'Date Started',
    fieldName: 'dateStarted',
    minWidth: 100,
    maxWidth: 100,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const dateStarted = momentUtc(item.dateStarted, StatDateFormats.TwoDigitDate, true);
      return <span>{dateStarted}</span>;
    },
  },
  {
    key: 'dateCompleted',
    name: 'Date Completed',
    fieldName: 'dateCompleted',
    minWidth: 100,
    maxWidth: 100,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const dateCompleted = momentUtc(item.dateCompleted, StatDateFormats.TwoDigitDate, true);
      return <span>{dateCompleted}</span>;
    },
  },
];

export default getColumns;
